/**
 * Reusable Design Patterns and Utilities
 *
 * Purpose: Collection of common design patterns, helper functions, and utilities
 *          for consistent implementation across the OrchestrateHQ AI frontend application.
 *
 * Architecture: Modular utility library with functional programming patterns,
 *              type-safe implementations, and performance optimizations.
 *
 * Dependencies: React, TypeScript, Performance optimizations
 * Author: Development Team
 * Created: 2025-08-08
 */

import { useEffect, useRef, useCallback } from 'react';

// =============================================================================
// TYPE UTILITIES
// =============================================================================

/**
 * Make specific properties optional in a type
 *
 * Purpose: Utility type for making certain properties optional
 * Usage: For creating update schemas from create schemas
 *
 * @example
 * type UserCreate = { name: string; email: string; age: number };
 * type UserUpdate = PartialBy<UserCreate, 'name' | 'age'>; // { name?: string; email: string; age?: number }
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Make specific properties required in a type
 *
 * Purpose: Utility type for making certain properties required
 * Usage: For ensuring critical fields are present
 *
 * @example
 * type User = { id?: string; name?: string; email?: string };
 * type ValidUser = RequiredBy<User, 'id' | 'email'>; // { id: string; name?: string; email: string }
 */
export type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/**
 * Deep readonly type utility
 *
 * Purpose: Makes all properties readonly recursively
 * Usage: For immutable data structures and configuration objects
 */
export type DeepReadonly<T> = {
  readonly [P in keyof T]: T[P] extends (infer U)[]
    ? DeepReadonlyArray<U>
    : T[P] extends object
      ? DeepReadonly<T[P]>
      : T[P];
};

type DeepReadonlyArray<T> = ReadonlyArray<DeepReadonly<T>>;

// =============================================================================
// TEXT PROCESSING UTILITIES
// =============================================================================

/**
 * Generate field definition ID from label
 *
 * Purpose: Convert human-readable field labels to URL-friendly, unique IDs
 * Logic: Converts to lowercase, replaces spaces with hyphens, removes special chars, adds fd_ prefix
 *
 * @param label - Human-readable field label
 * @returns Generated field definition ID (max 50 chars)
 *
 * @example
 * generateFieldDefinitionId("Campaign Name") // "fd_campaign-name"
 * generateFieldDefinitionId("Budget & Cost!") // "fd_budget-cost"
 * generateFieldDefinitionId("Complex Field @#$%") // "fd_complex-field"
 */
export const generateFieldDefinitionId = (label: string): string => {
  if (!label || typeof label !== 'string') {
    return '';
  }

  // Convert to lowercase and remove special characters (keep alphanumeric and spaces)
  const cleaned = label.toLowerCase().replace(/[^a-z0-9\s]/g, '');

  // Replace multiple spaces with single hyphens and trim
  const kebabCase = cleaned.replace(/\s+/g, '-').replace(/^-+|-+$/g, '');

  // Add fd_ prefix
  const fieldId = `fd_${kebabCase}`;

  // Limit to 50 characters
  return fieldId.length > 50 ? fieldId.substring(0, 50) : fieldId;
};

/**
 * Generate a workflow ID from a label
 *
 * Purpose: Auto-generate wf_ prefixed workflow IDs from human-readable labels
 * Logic: Same algorithm as generateFieldDefinitionId but with wf_ prefix
 *
 * @param label - Human-readable workflow label
 * @returns Generated workflow ID (max 50 chars)
 *
 * @example
 * generateWorkflowId("Media Approval") // "wf_media-approval"
 * generateWorkflowId("Campaign Launch!") // "wf_campaign-launch"
 */
export const generateWorkflowId = (label: string): string => {
  if (!label || typeof label !== 'string') {
    return '';
  }

  const cleaned = label.toLowerCase().replace(/[^a-z0-9\s]/g, '');
  const kebabCase = cleaned.replace(/\s+/g, '-').replace(/^-+|-+$/g, '');
  const workflowId = `wf_${kebabCase}`;

  return workflowId.length > 50 ? workflowId.substring(0, 50) : workflowId;
};

/**
 * Generate an AI Agent ID from a label
 *
 * Purpose: Auto-generate aa_ prefixed AI Agent IDs from human-readable labels
 * Logic: Same algorithm as generateFieldDefinitionId but with aa_ prefix
 *
 * @param label - Human-readable AI Agent label
 * @returns Generated AI Agent ID (max 50 chars)
 *
 * @example
 * generateAIAgentId("Brief Extractor") // "aa_brief-extractor"
 * generateAIAgentId("Plan Reviewer!")  // "aa_plan-reviewer"
 */
export const generateAIAgentId = (label: string): string => {
  if (!label || typeof label !== 'string') {
    return '';
  }

  const cleaned = label.toLowerCase().replace(/[^a-z0-9\s]/g, '');
  const kebabCase = cleaned.replace(/\s+/g, '-').replace(/^-+|-+$/g, '');
  const aiAgentId = `aa_${kebabCase}`;

  return aiAgentId.length > 50 ? aiAgentId.substring(0, 50) : aiAgentId;
};

/**
 * Generate a workflow step_key from a label
 *
 * Purpose: Auto-generate the step_key slug (ORCH-286 §5.3) from a human-readable
 *          label. Unlike workflow/AI-agent IDs, step_key has no fixed prefix and
 *          must start with a lowercase letter and use underscores, not hyphens
 *          (`^[a-z][a-z0-9_]*$` — it keys persisted run state).
 *
 * @param label - Human-readable step label
 * @returns Generated step_key (max 50 chars)
 *
 * @example
 * generateStepKey("Competitor Scan") // "competitor_scan"
 * generateStepKey("3rd Review!")     // "s_3rd_review"
 */
export const generateStepKey = (label: string): string => {
  if (!label || typeof label !== 'string') {
    return '';
  }

  const cleaned = label.toLowerCase().replace(/[^a-z0-9\s]/g, '');
  const snakeCase = cleaned.trim().replace(/\s+/g, '_');
  const stepKey = /^[a-z]/.test(snakeCase) ? snakeCase : `s_${snakeCase}`;

  return stepKey.length > 50 ? stepKey.substring(0, 50) : stepKey;
};

// =============================================================================
// FUNCTIONAL PROGRAMMING UTILITIES
// =============================================================================

/**
 * Compose functions from right to left
 *
 * Purpose: Function composition for creating complex operations from simple ones
 * Logic: Applies functions in reverse order, passing result to next function
 *
 * @param functions - Functions to compose
 * @returns Composed function
 *
 * @example
 * const addOne = (x: number) => x + 1;
 * const double = (x: number) => x * 2;
 * const addOneThenDouble = compose(double, addOne);
 * console.log(addOneThenDouble(3)); // 8 (3 + 1 = 4, 4 * 2 = 8)
 */
export const compose = <T>(...functions: Array<(arg: T) => T>) => {
  return (value: T): T => functions.reduceRight((acc, fn) => fn(acc), value);
};

/**
 * Pipe functions from left to right
 *
 * Purpose: Function composition for creating pipelines of transformations
 * Logic: Applies functions in order, passing result to next function
 *
 * @param value - Initial value
 * @param functions - Functions to apply in sequence
 * @returns Final transformed value
 *
 * @example
 * const addOne = (x: number) => x + 1;
 * const double = (x: number) => x * 2;
 * const result = pipe(3, addOne, double); // 8 (3 + 1 = 4, 4 * 2 = 8)
 */
export const pipe = <T>(value: T, ...functions: Array<(arg: T) => T>): T => {
  return functions.reduce((acc, fn) => fn(acc), value);
};

/**
 * Curry function to enable partial application
 *
 * Purpose: Transforms multi-argument function into sequence of single-argument functions
 * Logic: Returns function that accepts arguments one at a time until all are provided
 *
 * @param fn - Function to curry
 * @returns Curried function
 *
 * @example
 * const add = (a: number, b: number, c: number) => a + b + c;
 * const curriedAdd = curry(add);
 * const add5 = curriedAdd(5);
 * const add5and3 = add5(3);
 * console.log(add5and3(2)); // 10
 */
export const curry = <T extends any[], R>(fn: (...args: T) => R) => {
  return (...args: any[]): any => {
    if (args.length >= fn.length) {
      return fn(...(args as T));
    }
    return (...nextArgs: any[]) => curry(fn)(...args, ...nextArgs);
  };
};

/**
 * Memoize function results for performance optimization
 *
 * Purpose: Caches function results to avoid expensive recalculations
 * Logic: Stores results by stringified arguments, returns cached value if available
 *
 * @param fn - Function to memoize
 * @param keyGenerator - Optional custom key generator for cache
 * @returns Memoized function
 *
 * @example
 * const expensiveCalculation = (n: number) => {
 *   console.log('Calculating...');
 *   return n * n * n;
 * };
 * const memoizedCalc = memoize(expensiveCalculation);
 * console.log(memoizedCalc(5)); // Logs "Calculating..." then 125
 * console.log(memoizedCalc(5)); // Just returns 125 (cached)
 */
export const memoize = <T extends (...args: any[]) => any>(
  fn: T,
  keyGenerator?: (...args: Parameters<T>) => string
): T => {
  const cache = new Map<string, ReturnType<T>>();

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);

    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = fn(...args);
    cache.set(key, result);
    return result;
  }) as T;
};

/**
 * Debounce function execution
 *
 * Purpose: Delays function execution until specified time has passed since last call
 * Logic: Cancels previous timeout and sets new one on each call
 *
 * @param fn - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 *
 * @example
 * const searchHandler = debounce((query: string) => {
 *   console.log('Searching for:', query);
 * }, 300);
 *
 * searchHandler('a'); // Will not execute
 * searchHandler('ab'); // Will not execute
 * searchHandler('abc'); // Will execute after 300ms
 */
export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Throttle function execution
 *
 * Purpose: Limits function execution to once per specified time period
 * Logic: Ignores calls that happen within the throttle period
 *
 * @param fn - Function to throttle
 * @param delay - Throttle period in milliseconds
 * @returns Throttled function
 *
 * @example
 * const scrollHandler = throttle(() => {
 *   console.log('Scroll event handled');
 * }, 100);
 *
 * window.addEventListener('scroll', scrollHandler);
 */
export const throttle = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let isThrottled = false;

  return (...args: Parameters<T>) => {
    if (isThrottled) return;

    fn(...args);
    isThrottled = true;
    setTimeout(() => {
      isThrottled = false;
    }, delay);
  };
};

// =============================================================================
// ARRAY UTILITIES
// =============================================================================

/**
 * Group array elements by key
 *
 * Purpose: Groups array elements into an object based on a key function
 * Logic: Applies key function to each element and groups by resulting key
 *
 * @param array - Array to group
 * @param keyFn - Function to extract grouping key
 * @returns Object with grouped elements
 *
 * @example
 * const users = [
 *   { name: 'Alice', role: 'admin' },
 *   { name: 'Bob', role: 'user' },
 *   { name: 'Carol', role: 'admin' }
 * ];
 * const grouped = groupBy(users, user => user.role);
 * // { admin: [Alice, Carol], user: [Bob] }
 */
export const groupBy = <T, K extends string | number | symbol>(
  array: T[],
  keyFn: (item: T) => K
): Record<K, T[]> => {
  return array.reduce(
    (groups, item) => {
      const key = keyFn(item);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
      return groups;
    },
    {} as Record<K, T[]>
  );
};

/**
 * Remove duplicates from array based on key function
 *
 * Purpose: Removes duplicate elements from array using custom comparison
 * Logic: Tracks seen keys and only includes first occurrence of each key
 *
 * @param array - Array to deduplicate
 * @param keyFn - Function to extract comparison key (optional)
 * @returns Array with duplicates removed
 *
 * @example
 * const users = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' },
 *   { id: 1, name: 'Alice Clone' }
 * ];
 * const unique = uniqueBy(users, user => user.id);
 * // [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }]
 */
export const uniqueBy = <T, K extends string | number>(
  array: T[],
  keyFn: (item: T) => K = (item: T) => item as unknown as K
): T[] => {
  const seen = new Set<K>();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Chunk array into smaller arrays of specified size
 *
 * Purpose: Splits large array into smaller chunks for processing or display
 * Logic: Creates subarrays of specified size, with last chunk potentially smaller
 *
 * @param array - Array to chunk
 * @param size - Size of each chunk
 * @returns Array of chunks
 *
 * @example
 * const numbers = [1, 2, 3, 4, 5, 6, 7];
 * const chunks = chunk(numbers, 3);
 * // [[1, 2, 3], [4, 5, 6], [7]]
 */
export const chunk = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

/**
 * Sort array by multiple criteria
 *
 * Purpose: Sorts array using multiple sort functions in priority order
 * Logic: Applies sort functions in order until non-zero comparison is found
 *
 * @param array - Array to sort
 * @param sortFns - Array of comparison functions
 * @returns Sorted array
 *
 * @example
 * const users = [
 *   { name: 'Alice', age: 30, role: 'admin' },
 *   { name: 'Bob', age: 25, role: 'user' },
 *   { name: 'Carol', age: 30, role: 'user' }
 * ];
 * const sorted = sortBy(users, [
 *   (a, b) => a.role.localeCompare(b.role), // Sort by role first
 *   (a, b) => b.age - a.age,                // Then by age descending
 *   (a, b) => a.name.localeCompare(b.name)  // Then by name ascending
 * ]);
 */
export const sortBy = <T>(array: T[], sortFns: Array<(a: T, b: T) => number>): T[] => {
  return [...array].sort((a, b) => {
    for (const sortFn of sortFns) {
      const result = sortFn(a, b);
      if (result !== 0) return result;
    }
    return 0;
  });
};

// =============================================================================
// OBJECT UTILITIES
// =============================================================================

/**
 * Deep clone object using structured cloning
 *
 * Purpose: Creates deep copy of object with all nested properties
 * Logic: Uses JSON serialization or structuredClone for deep copying
 *
 * @param obj - Object to clone
 * @returns Deep cloned object
 *
 * @example
 * const original = { a: 1, b: { c: 2, d: [3, 4] } };
 * const cloned = deepClone(original);
 * cloned.b.c = 99; // Original is not affected
 */
export const deepClone = <T>(obj: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }
  // Fallback for older browsers
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Deep merge objects recursively
 *
 * Purpose: Merges multiple objects with deep property merging
 * Logic: Recursively merges nested objects and arrays
 *
 * @param objects - Objects to merge
 * @returns Merged object
 *
 * @example
 * const obj1 = { a: 1, b: { x: 1, y: 2 } };
 * const obj2 = { b: { y: 3, z: 4 }, c: 5 };
 * const merged = deepMerge(obj1, obj2);
 * // { a: 1, b: { x: 1, y: 3, z: 4 }, c: 5 }
 */
export const deepMerge = <T extends Record<string, any>>(...objects: Partial<T>[]): T => {
  const result = {} as T;

  for (const obj of objects) {
    for (const key in obj) {
      const value = obj[key];
      const existing = result[key];

      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        existing &&
        typeof existing === 'object' &&
        !Array.isArray(existing)
      ) {
        (result as any)[key] = deepMerge(existing, value);
      } else if (value !== undefined) {
        (result as any)[key] = value;
      }
    }
  }

  return result;
};

/**
 * Pick specified properties from object
 *
 * Purpose: Creates new object with only specified properties
 * Logic: Extracts only the properties listed in keys array
 *
 * @param obj - Source object
 * @param keys - Keys to pick
 * @returns Object with picked properties
 *
 * @example
 * const user = { id: 1, name: 'Alice', email: 'alice@example.com', password: 'secret' };
 * const publicUser = pick(user, ['id', 'name', 'email']);
 * // { id: 1, name: 'Alice', email: 'alice@example.com' }
 */
export const pick = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
};

/**
 * Omit specified properties from object
 *
 * Purpose: Creates new object without specified properties
 * Logic: Extracts all properties except those listed in keys array
 *
 * @param obj - Source object
 * @param keys - Keys to omit
 * @returns Object without omitted properties
 *
 * @example
 * const user = { id: 1, name: 'Alice', email: 'alice@example.com', password: 'secret' };
 * const publicUser = omit(user, ['password']);
 * // { id: 1, name: 'Alice', email: 'alice@example.com' }
 */
export const omit = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
};

// =============================================================================
// REACT UTILITIES
// =============================================================================

/**
 * Custom hook for previous value tracking
 *
 * Purpose: Tracks the previous value of a variable across renders
 * Logic: Uses useRef to store previous value and updates on each render
 *
 * @param value - Current value to track
 * @returns Previous value
 *
 * @example
 * const [count, setCount] = useState(0);
 * const prevCount = usePrevious(count);
 * // prevCount will be the count from the previous render
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

/**
 * Custom hook for stable callback references
 *
 * Purpose: Creates stable callback reference that doesn't change between renders
 * Logic: Uses useCallback with empty dependencies and useRef for latest values
 *
 * @param callback - Callback function to stabilize
 * @returns Stable callback reference
 *
 * @example
 * const handleClick = useStableCallback((id: string) => {
 *   // This reference won't change between renders
 *   onItemClick(id, currentValue);
 * });
 */
export const useStableCallback = <T extends (...args: any[]) => any>(callback: T): T => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useCallback(((...args: Parameters<T>) => callbackRef.current(...args)) as T, []);
};

// Note: useLocalStorage is available in utils/ui.ts to avoid duplication

// =============================================================================
// PERFORMANCE UTILITIES
// =============================================================================

/**
 * Lazy evaluation wrapper
 *
 * Purpose: Defers expensive computation until value is actually needed
 * Logic: Uses getter function that caches result after first evaluation
 *
 * @param fn - Function to evaluate lazily
 * @returns Lazy value wrapper
 *
 * @example
 * const expensiveValue = lazy(() => {
 *   console.log('Computing expensive value...');
 *   return heavyComputation();
 * });
 *
 * // Computation doesn't happen until you call .value
 * console.log(expensiveValue.value); // Logs "Computing..." then result
 * console.log(expensiveValue.value); // Just returns cached result
 */
export const lazy = <T>(fn: () => T): { readonly value: T } => {
  let hasValue = false;
  let value: T;

  return {
    get value() {
      if (!hasValue) {
        value = fn();
        hasValue = true;
      }
      return value;
    },
  };
};

/**
 * Batch multiple function calls into a single execution
 *
 * Purpose: Improves performance by batching multiple calls together
 * Logic: Collects function calls and executes them together in next tick
 *
 * @param fn - Function to batch
 * @returns Batched function
 *
 * @example
 * const batchedUpdate = batch((updates: string[]) => {
 *   console.log('Processing batch:', updates);
 *   // Process all updates together
 * });
 *
 * batchedUpdate(['update1']);
 * batchedUpdate(['update2']);
 * batchedUpdate(['update3']);
 * // All three calls will be batched into one execution
 */
export const batch = <T>(fn: (items: T[]) => void): ((item: T) => void) => {
  let pending: T[] = [];
  let scheduled = false;

  return (item: T) => {
    pending.push(item);

    if (!scheduled) {
      scheduled = true;
      Promise.resolve().then(() => {
        const items = pending;
        pending = [];
        scheduled = false;
        fn(items);
      });
    }
  };
};

export default {
  // Type utilities
  // (types are not runtime values, so not exported here)

  // Functional utilities
  compose,
  pipe,
  curry,
  memoize,
  debounce,
  throttle,

  // Array utilities
  groupBy,
  uniqueBy,
  chunk,
  sortBy,

  // Object utilities
  deepClone,
  deepMerge,
  pick,
  omit,

  // React utilities
  usePrevious,
  useStableCallback,
  // Note: useLocalStorage is exported from utils/ui.ts

  // Performance utilities
  lazy,
  batch,
};
